import { useGetFormalOffer } from '@api/lendingAPI/lapiHooks';
import { Spinner } from '@iwoca/orion';
import { RootState } from '@iwProvider/configureStore';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './Success.module.css';
import { FormattedCurrency } from '../../../../iwIntlProvider/FormattedCurrency';
import { SuccessCircleSVG } from '../../../../svgComponents/successCircleSVG';
import { getDrawdownType } from '../../../drawDown/getDrawdownType';
import { DRAW_DOWN_MODE } from '../../../funding/ConfirmFundingView/ConfirmFunding.sagas';
import FallbackErrorModal from '../../fallbackErrorModal/FallbackErrorModal';
import { Content } from '../Content';
import { PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD } from '../ProcessingPaymentModal.actions';

const mapStateToProps = (state: RootState) => ({
    ...state.processingPaymentModal
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    goToHome: () =>
        dispatch({ type: PROCESSING_PAYMENT_MODAL_RETURN_TO_DASHBOARD })
});

type PropsFromRedux = ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;
const Success = ({
    goToHome,
    senderLogo,
    receiverLogo
}: PropsFromRedux & {
    senderLogo: string;
    receiverLogo: string;
}) => {
    return (
        <Content
            title={<SuccessTitle />}
            icon={
                <span className={styles.processingDirectionSuccess}>
                    <SuccessCircleSVG
                        className={styles.SuccessCircleSVG}
                        data-testid="success-payment-icon"
                    />
                </span>
            }
            senderLogo={senderLogo}
            receiverLogo={receiverLogo}
            ctaOnClick={goToHome}
            ctaText={
                <FormattedMessage
                    id="_.navigation.return_to_dashboard"
                    defaultMessage="Return to dashboard"
                />
            }
        />
    );
};

const SuccessTitle = connect(mapStateToProps)(({
    mode,
    amount,
    paymentMethod,
    offerId
}) => {
    const formattedAmount = <FormattedCurrency value={amount!} />;

    if (mode !== DRAW_DOWN_MODE) {
        return (
            <>
                <p>
                    <FormattedMessage
                        id="_.processing_payment.modal.early_repayment.confirmed"
                        defaultMessage="{amount} has been repaid by your debit card ending {paymentMethod}"
                        values={{
                            amount: formattedAmount,
                            paymentMethod: paymentMethod
                        }}
                    />
                </p>
                <p className={styles.subtitle}>
                    <FormattedMessage
                        id="_.processing_payment.modal.early_repayment.subtitle"
                        defaultMessage="Your outstanding balance may take a moment to update"
                    />
                </p>
            </>
        );
    }

    return (
        <DrawdownLoading formattedAmount={formattedAmount} offerId={offerId!} />
    );
});

// eslint-disable-next-line import/no-default-export
export default connect(mapStateToProps, mapDispatchToProps)(Success);

const DrawdownLoading = ({
    formattedAmount,
    offerId
}: {
    formattedAmount: React.ReactNode;
    offerId: string;
}) => {
    const { formalOffer, loadingFormalOffer, formalOfferError } =
        useGetFormalOffer(offerId);

    if (loadingFormalOffer) return <Spinner />;
    if (formalOfferError) return <FallbackErrorModal />;

    const drawdownType = getDrawdownType(formalOffer!);

    switch (drawdownType) {
        case 'repayment_reduction':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.repayment_reduction.confirmed"
                    defaultMessage="You have successfully reduced your monthly repayments"
                />
            );
        case 'interest_only_extension':
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.interest_only_extension.confirmed"
                    defaultMessage="We've extended your interest-only period"
                />
            );
        case 'topup':
        default:
            return (
                <FormattedMessage
                    id="_.processing_payment.modal.draw_down.confirmed"
                    defaultMessage="We've sent {amount} to your bank account"
                    values={{ amount: formattedAmount }}
                />
            );
    }
};
